import React from 'react';
import { stringNormalize } from '../../Shared/quill-marking-logic/src/libs/stringNormalize';

import { extractBlankValues } from './extractBlankValues';

import { splitPromptForFillInBlank } from '../../Shared/libs/splitPromptForFillInBlank';

/**
 * Create HTML string with blank values bolded in response text.
 * The response text is the full sentence with blanks filled in.
 * This version returns an HTML string for use with dangerouslySetInnerHTML.
 *
 * @param responseText - The student's full response text (e.g., "He is a good friend")
 * @param prompt - The question prompt with blanks marked as ___ (e.g., "He is ___ ___ friend.")
 * @returns HTML string with blank values wrapped in <strong> tags
 *
 * @example
 * boldCuesInTextHTML("He is a good friend", "He is ___ ___ friend.")
 * // Returns: "He is <strong>a</strong> <strong>good</strong> friend"
 */

function blankValuePresentOrEmptyString(blankValue) {
  return blankValue || blankValue === ''
}

export function boldCuesInTextHTML(
  responseText: string,
  prompt: string
): string {
  if (!responseText || !prompt) {
    return responseText || '';
  }

  const blankValues = extractBlankValues(responseText, prompt);

  if (blankValues.length === 0) {
    return responseText;
  }

  const promptParts = splitPromptForFillInBlank(prompt);

  let result = '';

  for (let i = 0; i < promptParts.length; i++) {
    // Normalize the prompt part to strip HTML and match how extractBlankValues works
    const promptPart = stringNormalize(promptParts[i]).trim();

    if (promptPart) {
      result += promptPart;
    }

    if (i < promptParts.length - 1) {
      const blankValue = blankValues[i];
      if (blankValue !== undefined) {
        if (promptPart && blankValuePresentOrEmptyString(blankValue)) {
          result += ' ';
        }
        if (blankValue) {
          result += `<strong>${blankValue}</strong>`;
        }
        if (blankValue && (i < promptParts.length - 2 || promptParts[i + 1].trim())) {
          result += ' ';
        }
      }
    }
  }

  return result;
}

/**
 * Create JSX with blank values bolded in response text.
 * The response text is the full sentence with blanks filled in.
 *
 * @param responseText - The student's full response text (e.g., "He is a good friend")
 * @param prompt - The question prompt with blanks marked as ___ (e.g., "He is ___ ___ friend.")
 * @returns JSX element with blank values wrapped in <strong> tags
 *
 * @example
 * boldCuesInText("He is a good friend", "He is ___ ___ friend.")
 * // Returns: <span>He is <strong>a</strong> <strong>good</strong> friend</span>
 */
export function boldCuesInText(
  responseText: string,
  prompt: string
): JSX.Element {
  if (!responseText || !prompt) {
    return <span>{responseText || ''}</span>;
  }

  const blankValues = extractBlankValues(responseText, prompt);

  if (blankValues.length === 0) {
    return <span>{responseText}</span>;
  }

  const promptParts = splitPromptForFillInBlank(prompt);

  const elements: React.ReactNode[] = [];

  for (let i = 0; i < promptParts.length; i++) {
    const promptPart = stringNormalize(promptParts[i]).trim();

    if (promptPart) {
      elements.push(promptPart);
    }

    if (i < promptParts.length - 1) {
      const blankValue = blankValues[i];
      if (blankValue !== undefined) {
        // Add space before blank if needed
        if (promptPart && blankValue) {
          elements.push(' ');
        }
        if (blankValue) {
          elements.push(<strong key={`blank-${i}`}>{blankValue}</strong>);
        }
        // Add space after blank if there's a next prompt part
        if (blankValue && (i < promptParts.length - 2 || promptParts[i + 1].trim())) {
          elements.push(' ');
        }
      }
    }
  }

  return <span>{elements}</span>;
}
